import { Can } from "@/casl-provider";
import { useCasaviSettingsGetQuery } from "@/common/api/casavi/settings/use-casavi-settings-get.query";
import { useCasaviSettingsUpdateMutation } from "@/common/api/casavi/settings/use-casavi-settings-update.mutation";
import { useCasaviSyncMutation } from "@/common/api/casavi/settings/use-casavi-sync.mutation";
import { cronToHuman } from "@/common/helpers/cron-to-human";
import { handleFormSubmission } from "@/common/helpers/form-submission";
import { CronJobPicker } from "@/components/cron-job-picker/cron-job-picker";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { CommandButton } from "@/components/command-palette/command-button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyValue } from "@/components/ui/empty-value";
import { Label } from "@/components/ui/label";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { InfoIcon, RefreshCwIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useId, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ApplicationSelector } from "./_components/application-selector";
import { CasaviSettingsSkeleton } from "./_components/casavi-settings-skeleton";
import { ToggleSettingRow, type ToggleSettingField } from "./_components/toggle-setting-row";

export function CasaviSettingsView(): ReactElement {
  const { t } = useTranslation(["casavi", "common"]);
  const pageTitle = t("casavi:CasaviSettings");
  const pageSubtitle = t("casavi:CasaviSettingsDescription");
  const cronEnabledId = useId();
  const cronScheduleId = useId();
  const runOnStartupId = useId();
  const dryRunId = useId();
  const applicationId = useId();
  const { data: settings, isLoading } = useCasaviSettingsGetQuery();
  const { mutateAsync, isPending } = useCasaviSettingsUpdateMutation();
  const { mutateAsync: triggerSync, isPending: isSyncing } = useCasaviSyncMutation();
  const [cronScheduleValue, setCronScheduleValue] = useState<string>("");

  const handleSyncNow = async (): Promise<void> => {
    await handleFormSubmission(() => triggerSync(), {
      success: t("casavi:CasaviSyncNowSuccess"),
      error: t("casavi:CasaviSyncNowError"),
    });
  };

  const handleToggle = async (field: ToggleSettingField, value: boolean): Promise<void> => {
    await handleFormSubmission(() => mutateAsync({ [field]: value }), {
      success: t("casavi:CasaviSettingsSuccessUpdate"),
      error: t("casavi:CasaviSettingsErrorUpdate"),
    });
  };

  const handleUpdateApplication = async (uuid?: string): Promise<void> => {
    if (!uuid || uuid === settings?.application?.uuid) {
      return;
    }

    await handleFormSubmission(() => mutateAsync({ application: uuid }), {
      success: t("casavi:CasaviSettingsSuccessUpdate"),
      error: t("casavi:CasaviSettingsErrorUpdate"),
    });
  };

  const handleCronScheduleChange = async (newValue: string): Promise<void> => {
    if (newValue && newValue !== settings?.cronSchedule) {
      await handleFormSubmission(
        () => mutateAsync({ cronSchedule: newValue }),
        {
          success: t("casavi:CasaviSettingsCronSuccessUpdate"),
          error: t("casavi:CasaviSettingsCronErrorUpdate"),
        },
        () => setCronScheduleValue(""),
      );
    }
  };

  const content = isLoading ? (
    <CasaviSettingsSkeleton />
  ) : settings ? (
    <Card>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <Label className="text-sm font-medium" htmlFor={applicationId}>
              {t("casavi:Application")}
            </Label>
            <div className="flex items-center gap-1">
              <p className="text-muted-foreground text-xs">{t("casavi:CasaviSettingsApplicationDescription")}</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="text-muted-foreground h-3 w-3 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-96">
                    <Trans i18nKey="casavi:CasaviSettingsApplicationHelp" components={{ strong: <strong /> }} />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <Can I="update" a="CasaviSettings">
            <ApplicationSelector
              value={settings.application?.uuid}
              onValueChange={(uuid) => void handleUpdateApplication(uuid)}
              disabled={isPending}
              className="w-full max-w-sm shrink-0"
              id={applicationId}
            />
          </Can>
          <Can not I="update" a="CasaviSettings">
            <span className="shrink-0 text-sm">{settings.application?.name || <EmptyValue />}</span>
          </Can>
        </div>

        <ToggleSettingRow
          checked={settings.cronEnabled}
          description={t("casavi:CasaviSettingsCronDescription")}
          field="cronEnabled"
          id={cronEnabledId}
          isPending={isPending}
          label={t("casavi:CasaviSettingsCronEnabled")}
          onToggle={handleToggle}
        />

        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <Label className="text-sm font-medium" htmlFor={cronScheduleId}>
              {t("casavi:CasaviSettingsCronSchedule")}
            </Label>
            <p className="text-muted-foreground text-xs">{t("casavi:CasaviSettingsCronScheduleDescription")}</p>
          </div>
          <Can I="update" a="CasaviSettings">
            <div className="flex shrink-0 items-center gap-2">
              {isPending ? <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} /> : null}
              <CronJobPicker
                value={cronScheduleValue || settings.cronSchedule}
                onChange={(newValue) => {
                  setCronScheduleValue(newValue);
                  void handleCronScheduleChange(newValue);
                }}
                disabled={isPending}
                className="w-full max-w-md"
              />
            </div>
          </Can>
          <Can not I="update" a="CasaviSettings">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-muted-foreground border-border bg-muted/30 shrink-0 cursor-not-allowed rounded-md border px-3 py-2 font-mono text-sm">
                    {settings.cronSchedule}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{cronToHuman(settings.cronSchedule)}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Can>
        </div>

        <ToggleSettingRow
          checked={settings.runOnStartup}
          description={t("casavi:CasaviSettingsRunOnStartupDescription")}
          field="runOnStartup"
          id={runOnStartupId}
          isPending={isPending}
          label={t("casavi:CasaviSettingsRunOnStartup")}
          onToggle={handleToggle}
        />

        <ToggleSettingRow
          checked={settings.dryRun}
          description={t("casavi:CasaviSettingsDryRunDescription")}
          field="dryRun"
          id={dryRunId}
          isPending={isPending}
          label={t("casavi:CasaviSettingsDryRun")}
          onToggle={handleToggle}
        />

        <Can I="update" a="CasaviSettings">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-medium">{t("casavi:CasaviSyncNow")}</p>
              <p className="text-muted-foreground text-xs">{t("casavi:CasaviSyncNowDescription")}</p>
            </div>
            <CommandButton
              commandId="casavi-sync-now"
              label={t("casavi:CasaviSyncNow")}
              icon={<RefreshCwIcon />}
              onClick={() => void handleSyncNow()}
              loading={isSyncing}
              className="shrink-0"
            />
          </div>
        </Can>
      </CardContent>
    </Card>
  ) : null;

  return (
    <Can I="read" a="CasaviSettings">
      <CenteredLayout variant={CenteredLayoutVariant.Default} title={pageTitle} subTitle={pageSubtitle}>
        {content}
      </CenteredLayout>
    </Can>
  );
}
